import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { EyeIcon, FilterX } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  useMeterDispatchDetail,
  getDispatchStatus,
  useMeterReaderList,
  useAssignConsumer,
  useUpdateMeterDispatchDetail,
} from "@features/mx/meterReads/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { PageHeader } from "@shared/components/PageHeader";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { logEvent } from "@shared/analytics/analytics";

interface TableRowData {
  id: number;
  route: string;
  meters: number;
  consumers: number;
  premises: number;
  readings: string;
  assignedTo: string;
  assignedDate: string;
  dueDate: string;
  status: string;
  pendingCount: number;
  completedCount: number;
  readingCount: number;
  runId: number;
  routeId: number;
  meterReaderCount: number;
  newStatus: string;
  meterReaderId: string[];
  meterReaderName: string[];
}

interface MeterDispatchResult {
  id: number;
  run_id: number;
  status: any;
  routeName: string;
  routeId: number;
  meterCount: number;
  consumerCount: number;
  premiseCount: number;
  meterReaderName: any;
  meterReaderId: string[];
  dueDate: any;
  assignDate: any;
  premises: string[];
  readingCount: number;
  pendingCount: number;
  completedCount: number;
  job_cards: string[];
  meterReaderCount: number;
  newStatus: string;
}

interface DispatchFilters {
  status: string;
  reader: string;
}

interface Reader {
  id: number;
  employee_id: string;
  name: string;
  email: string;
  assigned_routes: number;
  contact_number: string;
  ratings: number;
  availability: string;
}

interface UpdateMeterDispatchPayload {
  is_assigned: boolean;
  reader_id: string[];
}

interface UpdateMeterDispatchResponse {
  id: string;
  dispatch_id: string;
  is_assigned: boolean;
  reader_id: number[];
  updated_at: string;
  message?: string;
}

const RoutesInCycleTable: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const meter_run_id = params.id;

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const statusFilter = searchParams.get("status") || "";
  const readerFilter = searchParams.get("reader") || "";

  const [pendingFilters, setPendingFilters] = useState<DispatchFilters>({
    status: decodeURIComponent(statusFilter),
    reader: decodeURIComponent(readerFilter),
  });

  // Modal states
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState<boolean>(false);
  const [selectedReaderIds, setSelectedReaderIds] = useState<string[]>([]);
  const [allReaderIds, setAllReaderIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [assignPage, setAssignPage] = useState(1);
  const [selectedDispatchId, setSelectedDispatchId] = useState<number | null>(
    null
  );
  const [selectedRouteInfo, setSelectedRouteInfo] = useState<{
    route: string;
    assignedTo: string;
  } | null>(null);

  useEffect(() => {
    setPendingFilters({
      status: decodeURIComponent(statusFilter),
      reader: decodeURIComponent(readerFilter),
    });
  }, [statusFilter, readerFilter]);

  const handlePendingFilterChange = (
    key: keyof DispatchFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Fetch data using the correct hook
  const {
    data: meterDispatchData,
    isLoading,
    error,
    refetch,
  } = useMeterDispatchDetail({
    meter_run_id: meter_run_id,
    remote_utility_id: remoteUtilityId,
    origin: "Web",
    page: currentPage,
    run_status: statusFilter,
    meter_reader_id: readerFilter,
    search: searchQuery,
  });

  const { data: dispatchStatusData } = getDispatchStatus();
  const { data: readersFilter } = useMeterReaderList(remoteUtilityId);
  const readersList = mapToLabelValue(readersFilter, "name", "id");

  const { data: meterAssignData } = useAssignConsumer({
    remote_utility_id: remoteUtilityId,
    isActive: "True",
    page: assignPage,
    search: searchText,
  });

  const { mutate: updateMeterDispatch, isPending: isUpdating } =
    useUpdateMeterDispatchDetail();

  // Transform API data for table
  const tableData: TableRowData[] = useMemo(() => {
    if (!meterDispatchData?.results) return [];

    return meterDispatchData.results.map(
      (item: MeterDispatchResult): TableRowData => ({
        id: item.id,
        route: item.routeName,
        meters: item.meterCount,
        consumers: item.consumerCount,
        premises: item.premiseCount,
        readings: `${item.readingCount}/${item.meterCount}`,
        assignedTo: item.meterReaderName,
        assignedDate: item.assignDate,
        dueDate: item.dueDate,
        status: item.status,
        pendingCount: item.pendingCount,
        completedCount: item.completedCount,
        readingCount: item.readingCount,
        runId: item.run_id,
        routeId: item.routeId,
        meterReaderCount: item.meterReaderCount || 0,
        newStatus: item.newStatus,
        meterReaderId: item?.meterReaderId || [],
        meterReaderName: item?.meterReaderName || [],
      })
    );
  }, [meterDispatchData]);

  // Apply search filtering
  const filteredData: TableRowData[] = useMemo(() => {
    let data = [...tableData];

    if (searchQuery.trim()) {
      data = data.filter((row) =>
        row.route.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return data;
  }, [tableData, searchQuery]);

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.status && { status: pendingFilters.status }),
      ...(pendingFilters.reader && { reader: pendingFilters.reader }),
    };

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.status;
    delete newParams.reader;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(statusFilter && { status: statusFilter }),
      ...(readerFilter && { reader: readerFilter }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (p: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: String(p),
    });
  };

  const handleUnassign = (row: TableRowData) => {
    setSelectedDispatchId(row.id);
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row?.meterReaderName?.join(", "),
    });
    fetchModalTableData();
    setUnassignModalOpen(true);
  };

  const handleAssign = (row: TableRowData) => {
    setSelectedDispatchId(row.id);
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row.assignedTo,
    });
    fetchModalTableData();
    setAssignModalOpen(true);
  };

  // Create filtered readers based on search
  const filteredReaders = useMemo(() => {
    if (!meterAssignData?.results) return [];

    const readers: Reader[] = meterAssignData.results.map((reader: any) => ({
      id: reader.id,
      employee_id: reader.employeeId,
      name: reader.name,
      email: reader.email,
      assigned_routes: reader.assignedRoutes,
      contact_number: reader.contactNumber,
      ratings: reader.ratings,
      availability: reader.availability,
    }));

    if (!searchText.trim()) return readers;

    return readers.filter(
      (reader) =>
        reader.name.toLowerCase().includes(searchText.toLowerCase()) ||
        reader.employee_id.toLowerCase().includes(searchText.toLowerCase()) ||
        reader.email.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [meterAssignData, searchText]);

  const fetchModalTableData = () => {
    if (meterAssignData?.results) {
      const readerIds = meterAssignData.results.map((reader: any) => reader.id);
      setAllReaderIds(readerIds);
    }
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    if (checked) {
      if (!selectedReaderIds.includes(id)) {
        setSelectedReaderIds([...selectedReaderIds, id]);
      }
    } else {
      setSelectedReaderIds(
        selectedReaderIds.filter((readerId) => readerId !== id)
      );
    }
  };

  const handleAssignModalClose = () => {
    setAssignModalOpen(false);
    setSelectedReaderIds([]);
    setSearchText("");
    setSelectedDispatchId(null);
  };

  const handleUnassignModalClose = () => {
    setUnassignModalOpen(false);
    setSelectedDispatchId(null);
    setSelectedRouteInfo(null);
    setAllReaderIds([]);
  };

  const handleAssignSubmit = () => {
    if (selectedDispatchId && selectedReaderIds.length > 0) {
      const payload: UpdateMeterDispatchPayload = {
        is_assigned: true,
        reader_id: selectedReaderIds,
      };

      updateMeterDispatch(
        {
          dispatchId: selectedDispatchId.toString(),
          payload,
        },
        {
          onSuccess: (data) => {
            console.log("Assignment successful:", data);
            logEvent("Route Assigned", {
              ...payload,
              route: selectedRouteInfo.route,
            });
            handleAssignModalClose();
            refetch();
          },
          onError: (error) => {
            console.error("Assignment failed:", error);
          },
        }
      );
    }
  };

  const handleUnassignSubmit = () => {
    if (selectedDispatchId && allReaderIds.length > 0) {
      const payload: UpdateMeterDispatchPayload = {
        is_assigned: false,
        reader_id: allReaderIds,
      };

      console.log("Unassign payload:", payload);

      updateMeterDispatch(
        {
          dispatchId: selectedDispatchId.toString(),
          payload,
        },
        {
          onSuccess: (data) => {
            console.log("Unassignment successful:", data);
            logEvent("Route Unassigned", {
              ...payload,
              route: selectedRouteInfo.route,
            });
            handleUnassignModalClose();
            refetch();
          },
          onError: (error) => {
            console.error("Unassignment failed:", error);
          },
        }
      );
    }
  };

  const handleReAssign = (row: TableRowData) => {
    setSelectedDispatchId(row.id);
    setSelectedRouteInfo({
      route: row.route,
      assignedTo: row.assignedTo,
    });

    // Preselect already assigned ids
    setSelectedReaderIds(row.meterReaderId);

    fetchModalTableData();
    setAssignModalOpen(true);
  };

  // Columns configuration
  const columns: TableColumn<TableRowData>[] = [
    {
      key: "route",
      header: "Route Name",
      headerTooltip:
        "Geographic route identifier. Routes group meters by location for efficient field operations.",
      sortable: true,
    },
    {
      key: "meters",
      header: "Meters",
      headerTooltip:
        "Total number of meters in this route. Used to estimate workload and time required.",
      sortable: true,
      responsive: { hidden: "sm:table-cell" },
    },
    {
      key: "premises",
      header: "Premises",
      headerTooltip:
        "Number of physical locations to visit. May differ from meter count if some premises have multiple meters.",
      sortable: true,
      responsive: { hidden: "md:table-cell" },
    },
    {
      key: "readings",
      headerTooltip:
        "Number of readings submitted vs total meters in route. Format: collected / total. Tracks route progress.",
      header: "Readings Collected",
      sortable: false,
    },
    {
      key: "assignedTo",
      header: "Assigned To",
      headerTooltip:
        'Count of meter reader assigned to this route. Shows "—" if unassigned. Contact this person for route-specific questions.',
      sortable: true,
      responsive: { hidden: "lg:table-cell" },
      render: (row) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer">{row?.length || 0}</div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-words">
              {row?.length > 0 ? row.join(", ") : "No items"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "assignedDate",
      header: "Assigned Date",
      headerTooltip:
        "Date when route was assigned to a meter reader. Used to track dispatch timing and workload distribution.",
      sortable: true,
      responsive: { hidden: "lg:table-cell" },
    },
    {
      key: "dueDate",
      header: "Due Date",
      headerTooltip:
        "Deadline for completing all readings in this route. Helps prioritize work and identify at-risk routes.",
      sortable: true,
    },
    {
      key: "newStatus",
      header: "Status",
      headerTooltip:
        "Route assignment status: Assigned (has a meter reader) or Unassigned (awaiting assignment).",
      type: "badge",
      render: (_, row) => (
        <Badge className={getStatusBadgeClass(row?.newStatus ?? "Unassigned")}>
          {row?.newStatus}
        </Badge>
      ),
    },
    //      {
    //   header: "Actions",
    //   key: "actions",
    //   render: (_, row) => {
    //    return (
    //   <>
    //     {row.status === "Not Dispatched" ? (
    //       <Button size="sm" variant="outline" onClick={() => handleAssign(row)}>
    //         Assign
    //       </Button>
    //     ) : (
    //       <span className="flex gap-2">
    //         <Button size="sm" onClick={() => handleReAssign(row)}>
    //           Re-assign
    //         </Button>
    //         <Button size="sm" onClick={() => handleUnassign(row)} className="bg-red-500 hover:bg-red-600">
    //           Unassign
    //         </Button>
    //       </span>
    //     )}
    //   </>
    // );
    //   },
    // },
  ];

  const actions: TableAction[] = [
    {
      label: "Assign",
      onClick: (row) => handleAssign(row),
      hidden: (row) => row.status !== "Not Dispatched", // show only when NOT_DISPATCHED
    },
    {
      label: "Re-assign",
      onClick: (row) => handleReAssign(row),
      hidden: (row) => row.status === "Not Dispatched", // hide ONLY when NOT_DISPATCHED
    },
    {
      label: "Un-assign",
      onClick: (row) => handleUnassign(row),
      hidden: (row) => row.status === "Not Dispatched", // hide ONLY when NOT_DISPATCHED
    },
  ];

  const assignColumns: TableColumn<Reader>[] = [
    { key: "employee_id", header: "Emp Id" },
    { key: "name", header: "Name" },
    { key: "email", header: "Contact" },
    { key: "assigned_routes", header: "Workload" },
  ];
  const [assignPageSize, setAssignPageSize] = useState(PAGE_SIZE);

  const totalItems = meterDispatchData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const hasActiveFilters = statusFilter || readerFilter;

  // Filter panel
  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={pendingFilters.status}
            onValueChange={(value) =>
              handlePendingFilterChange("status", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent>
              {dispatchStatusData?.result?.map((status: any) => (
                <SelectItem key={status.key} value={String(status.key)}>
                  {status.value}
                </SelectItem>
              )) || []}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Reader</label>
          <Select
            value={pendingFilters.reader}
            onValueChange={(value) =>
              handlePendingFilterChange("reader", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Reader" />
            </SelectTrigger>
            <SelectContent>
              {readersList?.map((reader: any) => (
                <SelectItem key={reader.value} value={String(reader.value)}>
                  {reader.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={resetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center text-red-600">
          Error loading meter dispatch data: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Routes"
        subtitle="View, search, filter and dispatch routes for this cycle."
      />
      <GenericTable
        data={filteredData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        actions={actions}
        actionsType="dropdown"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
          endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by route name..."
        filterPanel={filterPanel}
        emptyMessage="No routes found matching your criteria"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {[statusFilter, readerFilter].filter(Boolean).length} filter(s)
                active
              </span>
            </div>
          ) : null
        }
      />

      {/* Assignment Modal */}
      <Dialog open={assignModalOpen} onOpenChange={handleAssignModalClose}>
        <DialogContent className="max-w-6xl max-h-[70vh] overflow-scroll">
          <DialogHeader>
            <DialogTitle>
              Assign Meter Reader to {selectedRouteInfo?.route}
            </DialogTitle>
            <DialogDescription>
              Select one or more readers to assign to this route.
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="mb-4">
            {/* <Input
              placeholder="Search by name, employee ID, or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="overflow-auto border rounded-lg max-h-96">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2">Select</th>
                  <th className="px-4 py-2">Emp Id</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Contact</th>
                  <th className="px-4 py-2">Workload</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {!meterAssignData ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      Loading readers...
                    </td>
                  </tr>
                ) : filteredReaders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {searchText
                        ? "No readers found matching your search"
                        : "No readers available"}
                    </td>
                  </tr>
                ) : (
                  filteredReaders.map((reader) => (
                    <tr key={reader.id}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedReaderIds.includes(reader.id)}
                          onChange={(e) =>
                            handleCheckboxChange(reader.id, e.target.checked)
                          }
                        />
                      </td>
                      <td className="px-4 py-2">{reader.employee_id}</td>
                      <td className="px-4 py-2">{reader.name}</td>
                      <td className="px-4 py-2">{reader.email}</td>
                      <td className="px-4 py-2">{reader.assigned_routes}</td>
                      <td className="px-4 py-2">{reader.ratings}</td>
                      <td className="px-4 py-2">{reader.availability}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table> */}

            <GenericTable
              data={filteredReaders}
              columns={assignColumns}
              rowKey="id"
              loading={!meterAssignData}
              pagination={{
                currentPage: assignPage,
                itemsPerPage: assignPageSize,
                totalItems: meterAssignData?.count || filteredReaders.length,
                totalPages: Math.ceil(
                  (meterAssignData?.count || filteredReaders.length) /
                    assignPageSize
                ),
                startItem: (assignPage - 1) * assignPageSize + 1,
                endItem: Math.min(
                  assignPage * assignPageSize,
                  filteredReaders.length
                ),
              }}
              onPageChange={setAssignPage}
              emptyMessage={
                searchText
                  ? "No readers found matching your search"
                  : "No readers available"
              }
              selectable={true}
              selectedRows={selectedReaderIds}
              onSelectionChange={(selectedIds) =>
                setSelectedReaderIds(selectedIds)
              }
              onSearch={(value) => setSearchText(value)}
              searchPlaceholder="Search meter reader by name or employee id..."
            />
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={handleAssignModalClose}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              disabled={selectedReaderIds.length === 0 || isUpdating}
              onClick={handleAssignSubmit}
            >
              {isUpdating
                ? "Assigning..."
                : `Assign Selected (${selectedReaderIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign Confirmation Modal */}
      <Dialog open={unassignModalOpen} onOpenChange={handleUnassignModalClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unassign Route</DialogTitle>
            <DialogDescription>
              Confirm that you want to unassign this route.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to unassign{" "}
              <strong>{selectedRouteInfo?.assignedTo}</strong> from{" "}
              <strong>{selectedRouteInfo?.route}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              This will change the status to 'Unassigned'
            </p>
            {allReaderIds.length > 0 && (
              <p className="text-xs text-blue-600">Will unassign reader(s)</p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={handleUnassignModalClose}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnassignSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? "Unassigning..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoutesInCycleTable;
